import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Trophy, 
  Plus,
  Users,
  Clock,
  Copy,
  LogIn,
  Crown,
  Loader2,
  ChevronRight,
  Trash2,
  LogOut,
  Lock,
  Globe,
  Medal,
  Sparkles,
  AlertCircle,
  BarChart3,
  TrendingUp,
  Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format time remaining
const formatTimeRemaining = (timeObj) => {
  if (!timeObj) return 'Ended';
  const { days, hours, minutes } = timeObj;
  if (days > 30) return `${Math.floor(days / 30)} months left`;
  if (days > 7) return `${Math.floor(days / 7)} weeks left`;
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

// Mini League Card Component
const MiniLeagueCard = ({ league, onViewDetails, onLeave, onDelete, onCopyCode, onViewAnalytics }) => {
  const isOwner = league.user_role === 'owner';
  
  return (
    <Card className="bg-gradient-to-br from-rose-950/30 to-slate-900 border-rose-500/20 hover:border-rose-500/40 transition-all" data-testid={`mini-league-${league.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-500/20">
              <Trophy className="h-5 w-5 text-rose-400" />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base text-white truncate">{league.name}</CardTitle>
              <CardDescription className="text-rose-200/60 text-sm">
                by {league.owner_username}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-1">
              {league.pro_only && (
                <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  <Crown className="h-3 w-3 mr-1" />Pro
                </Badge>
              )}
              <Badge className={league.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                {league.status}
              </Badge>
            </div>
            {isOwner && (
              <Badge variant="outline" className="text-xs text-rose-400 border-rose-500/30">
                Owner
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {league.description && (
          <p className="text-sm text-slate-400 line-clamp-2">{league.description}</p>
        )}
        
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-lg bg-slate-800/50">
            <div className="text-lg font-bold text-white">{league.your_points || 0}</div>
            <div className="text-xs text-slate-400">Points</div>
          </div>
          <div className="p-2 rounded-lg bg-slate-800/50">
            <div className="text-lg font-bold text-rose-400">#{league.your_rank || '-'}</div>
            <div className="text-xs text-slate-400">Rank</div>
          </div>
          <div className="p-2 rounded-lg bg-slate-800/50">
            <div className="text-lg font-bold text-white">{league.member_count}</div>
            <div className="text-xs text-slate-400">/{league.max_members}</div>
          </div>
        </div>
        
        <div className="flex items-center justify-between text-sm text-slate-400 bg-slate-800/30 rounded-lg p-2">
          <span className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {formatTimeRemaining(league.time_remaining)}
          </span>
          <span className="flex items-center gap-1">
            {league.is_public ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            {league.is_public ? 'Public' : 'Private'}
          </span>
        </div>
        
        {/* Invite Code for owner/admin */}
        {league.invite_code && (
          <div className="flex items-center gap-2 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2">
            <span className="text-sm text-slate-400">Code:</span>
            <code className="font-mono text-rose-400 flex-1">{league.invite_code}</code>
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-8 px-2 text-rose-400 hover:text-rose-300"
              onClick={() => onCopyCode(league.invite_code)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="flex-1 border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
            onClick={() => onViewDetails(league)}
            data-testid={`view-leaderboard-${league.id}`}
          >
            Leaderboard
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            onClick={() => onViewAnalytics(league)}
            title="Analytics"
            data-testid={`view-analytics-${league.id}`}
          >
            <BarChart3 className="h-4 w-4" />
          </Button>
          {isOwner ? (
            <Button 
              variant="outline" 
              size="icon"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={() => onDelete(league)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              variant="outline" 
              size="icon"
              className="border-slate-600 text-slate-400 hover:bg-slate-700"
              onClick={() => onLeave(league)}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Create Mini League Dialog
const CreateMiniLeagueDialog = ({ open, onClose, limits, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [maxMembers, setMaxMembers] = useState(20);
  const [durationWeeks, setDurationWeeks] = useState(13);
  const [isPublic, setIsPublic] = useState(false);
  const [proOnly, setProOnly] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a league name');
      return;
    }
    
    setCreating(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || null,
        max_members: maxMembers,
        duration_weeks: durationWeeks,
        is_public: isPublic,
        pro_only: proOnly
      });
      // Reset form
      setName('');
      setDescription('');
      setMaxMembers(20);
      setDurationWeeks(13);
      setIsPublic(false);
      setProOnly(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plus className="h-5 w-5 text-rose-400" />
            Create Mini League
          </DialogTitle>
          <DialogDescription>
            Create a private competition where everyone starts fresh
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">League Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Office Champions"
              className="bg-slate-800 border-slate-700"
              maxLength={50}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this league about?"
              className="bg-slate-800 border-slate-700 resize-none"
              rows={2}
              maxLength={500}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Max Players</Label>
              <Select value={String(maxMembers)} onValueChange={(v) => setMaxMembers(Number(v))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 players</SelectItem>
                  <SelectItem value="20">20 players</SelectItem>
                  <SelectItem value="50">50 players</SelectItem>
                  <SelectItem value="100">100 players</SelectItem>
                  {limits?.is_pro && (
                    <>
                      <SelectItem value="200">200 players</SelectItem>
                      <SelectItem value="500">500 players</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {!limits?.is_pro && maxMembers > 20 && (
                <p className="text-xs text-amber-400">Pro required for more than 20 players</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select value={String(durationWeeks)} onValueChange={(v) => setDurationWeeks(Number(v))}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">1 month</SelectItem>
                  <SelectItem value="8">2 months</SelectItem>
                  <SelectItem value="13">3 months</SelectItem>
                  <SelectItem value="17">4 months</SelectItem>
                  <SelectItem value="22">5 months</SelectItem>
                  <SelectItem value="26">6 months (max)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
            <div>
              <Label className="text-white">Public League</Label>
              <p className="text-xs text-slate-400">Anyone can find and join</p>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={setIsPublic}
            />
          </div>
          
          {limits?.is_pro && (
            <div className="flex items-center justify-between p-3 bg-amber-950/30 border border-amber-500/20 rounded-lg">
              <div>
                <Label className="text-amber-300 flex items-center gap-2">
                  <Crown className="h-4 w-4" /> Pro-Only League
                </Label>
                <p className="text-xs text-slate-400">Only Pro members can join this league</p>
              </div>
              <Switch
                checked={proOnly}
                onCheckedChange={setProOnly}
                data-testid="pro-only-toggle"
              />
            </div>
          )}
          
          <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Sparkles className="h-5 w-5 text-rose-400 mt-0.5" />
              <div>
                <p className="text-sm text-rose-300 font-medium">Fresh Start Rules</p>
                <p className="text-xs text-slate-400 mt-1">
                  All participants start at V$100,000 and 0 points. Your main portfolio is unaffected.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button 
            className="bg-rose-600 hover:bg-rose-500"
            onClick={handleCreate}
            disabled={creating || !name.trim()}
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create League
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Join Mini League Dialog
const JoinMiniLeagueDialog = ({ open, onClose, onJoin }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    
    setJoining(true);
    try {
      await onJoin(inviteCode.trim().toUpperCase());
      setInviteCode('');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <LogIn className="h-5 w-5 text-rose-400" />
            Join Mini League
          </DialogTitle>
          <DialogDescription>
            Enter the invite code shared by the league owner
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="code">Invite Code</Label>
            <Input
              id="code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="e.g., ABC12345"
              className="bg-slate-800 border-slate-700 font-mono text-lg tracking-wider"
              maxLength={12}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={joining}>
            Cancel
          </Button>
          <Button 
            className="bg-rose-600 hover:bg-rose-500"
            onClick={handleJoin}
            disabled={joining || !inviteCode.trim()}
          >
            {joining ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4 mr-2" />
                Join League
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Leaderboard Entry Row
const LeaderboardRow = ({ entry }) => {
  const getRankColor = (rank) => {
    if (rank === 1) return 'text-amber-400';
    if (rank === 2) return 'text-slate-300';
    if (rank === 3) return 'text-amber-600';
    return 'text-slate-400';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-amber-400" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-300" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return null;
  };

  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg ${
        entry.is_you 
          ? 'bg-rose-500/10 border border-rose-500/30' 
          : 'bg-slate-800/30 hover:bg-slate-800/50'
      } transition-colors`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-8 text-center font-bold ${getRankColor(entry.rank)}`}>
          {getRankIcon(entry.rank) || `#${entry.rank}`}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={`font-medium ${entry.is_you ? 'text-rose-400' : 'text-white'}`}>
              {entry.username}
            </span>
            {entry.is_you && (
              <Badge className="bg-rose-500/20 text-rose-400 text-xs">You</Badge>
            )}
          </div>
          <div className="text-xs text-slate-500">{entry.weeks_played} weeks played</div>
        </div>
      </div>
      <div className="text-right">
        <div className="font-bold text-white">{entry.total_points}</div>
        <div className="text-xs text-slate-500">points</div>
      </div>
    </div>
  );
};

// League Analytics Dialog
const LeagueAnalyticsDialog = ({ league, open, onClose, token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && league) {
      fetchAnalytics();
    }
  }, [open, league?.id]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/mini-leagues/${league.id}/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setAnalytics(await res.json());
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-400" />
            {league?.name} Analytics
          </DialogTitle>
          <DialogDescription>
            Performance insights for this league
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : analytics ? (
          <div className="space-y-4 overflow-y-auto max-h-[55vh] pr-2" data-testid="league-analytics">
            {/* Overview Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <div className="text-2xl font-bold text-white">{analytics.total_members}</div>
                <div className="text-xs text-slate-400">Members</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <div className="text-2xl font-bold text-blue-400">{analytics.avg_points}</div>
                <div className="text-xs text-slate-400">Avg Points</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <div className="text-2xl font-bold text-emerald-400">{analytics.activity_rate}%</div>
                <div className="text-xs text-slate-400">Active Rate</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 text-center">
                <div className="text-2xl font-bold text-amber-400">{analytics.avg_weeks_played}</div>
                <div className="text-xs text-slate-400">Avg Weeks</div>
              </div>
            </div>

            {/* Your Analytics */}
            {analytics.your_analytics && (
              <div className="p-4 rounded-lg bg-blue-950/30 border border-blue-500/20">
                <h4 className="text-blue-400 font-medium mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" /> Your Performance
                </h4>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-lg font-bold text-white">#{analytics.your_analytics.rank}</div>
                    <div className="text-xs text-slate-400">Rank</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{analytics.your_analytics.total_points}</div>
                    <div className="text-xs text-slate-400">Points</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-emerald-400">Top {analytics.your_analytics.percentile}%</div>
                    <div className="text-xs text-slate-400">Percentile</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-500/20 flex items-center justify-between text-sm">
                  <span className="text-slate-400">vs League Average</span>
                  <span className={analytics.your_analytics.vs_average >= 0 ? 'text-emerald-400 font-medium' : 'text-red-400 font-medium'}>
                    {analytics.your_analytics.vs_average >= 0 ? '+' : ''}{analytics.your_analytics.vs_average} pts
                  </span>
                </div>
              </div>
            )}

            {/* Top Performers */}
            {analytics.top_performers?.length > 0 && (
              <div>
                <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-400" /> Top Performers
                </h4>
                <div className="space-y-2">
                  {analytics.top_performers.map((p) => (
                    <div key={p.rank} className={`flex items-center justify-between p-2 rounded-lg ${
                      p.is_you ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-slate-800/30'
                    }`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-6 text-center font-bold ${
                          p.rank === 1 ? 'text-amber-400' : p.rank === 2 ? 'text-slate-300' : p.rank === 3 ? 'text-amber-600' : 'text-slate-400'
                        }`}>#{p.rank}</span>
                        <span className={p.is_you ? 'text-blue-400 font-medium' : 'text-white'}>
                          {p.username} {p.is_you && <Badge className="bg-blue-500/20 text-blue-400 text-xs ml-1">You</Badge>}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-medium">{p.total_points} pts</span>
                        <span className="text-slate-500 text-xs ml-2">{p.average}/wk</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Point Distribution */}
            {analytics.point_distribution?.length > 0 && (
              <div>
                <h4 className="text-white font-medium mb-2">Point Distribution</h4>
                <div className="space-y-1.5">
                  {analytics.point_distribution.filter(b => b.count > 0).map((bucket) => {
                    const maxCount = Math.max(...analytics.point_distribution.map(b => b.count), 1);
                    const width = Math.max(8, (bucket.count / maxCount) * 100);
                    return (
                      <div key={bucket.range} className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-16 text-right">{bucket.range}</span>
                        <div className="flex-1 bg-slate-800 rounded-full h-5 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-rose-500/80 to-rose-400/60 rounded-full flex items-center justify-end px-2"
                            style={{ width: `${width}%` }}
                          >
                            <span className="text-xs text-white font-medium">{bucket.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">No analytics data available</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Mini Leagues Dashboard Component
const MiniLeaguesDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState([]);
  const [limits, setLimits] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [analyticsLeague, setAnalyticsLeague] = useState(null);
  const [leagueOfWeek, setLeagueOfWeek] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      
      // Fetch user's mini leagues, limits, and league of the week in parallel
      const [leaguesRes, limitsRes, lotwRes] = await Promise.all([
        fetch(`${API_URL}/api/mini-leagues/my-leagues`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/mini-leagues/limits`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/mini-leagues/league-of-the-week`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const leaguesData = await leaguesRes.json();
      const limitsData = await limitsRes.json();
      const lotwData = await lotwRes.json();
      
      setLeagues(leaguesData.leagues || []);
      setLimits(limitsData);
      setLeagueOfWeek(lotwData.league || null);
    } catch (err) {
      console.error('Error fetching mini leagues:', err);
      toast.error('Failed to load mini leagues');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/mini-leagues/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      const result = await res.json();
      
      if (res.ok) {
        toast.success(`Created "${data.name}"! Share code: ${result.invite_code}`);
        setShowCreate(false);
        fetchData();
      } else {
        toast.error(result.detail || 'Failed to create league');
      }
    } catch (err) {
      console.error('Error creating league:', err);
      toast.error('Failed to create league');
    }
  };

  const handleJoin = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/mini-leagues/join?invite_code=${code}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await res.json();
      
      if (res.ok) {
        toast.success(result.message || 'Successfully joined!');
        setShowJoin(false);
        fetchData();
      } else {
        toast.error(result.detail || 'Failed to join league');
      }
    } catch (err) {
      console.error('Error joining league:', err);
      toast.error('Failed to join league');
    }
  };

  const handleLeave = async (league) => {
    try {
      const res = await fetch(`${API_URL}/api/mini-leagues/${league.id}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Left ${league.name}`);
        fetchData();
      } else {
        const result = await res.json();
        toast.error(result.detail || 'Failed to leave league');
      }
    } catch (err) {
      toast.error('Failed to leave league');
    }
  };

  const handleDelete = async (league) => {
    try {
      const res = await fetch(`${API_URL}/api/mini-leagues/${league.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Deleted ${league.name}`);
        setConfirmDelete(null);
        fetchData();
      } else {
        const result = await res.json();
        toast.error(result.detail || 'Failed to delete league');
      }
    } catch (err) {
      toast.error('Failed to delete league');
    }
  };

  const handleViewDetails = async (league) => {
    setSelectedLeague(league);
    setLeaderboardLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/mini-leagues/${league.id}/leaderboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLeaderboard(data);
    } catch (err) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-rose-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mini-leagues-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Trophy className="h-6 w-6 text-rose-400" />
            Mini Leagues
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Create or join private competitions with friends
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            className="border-slate-700"
            onClick={() => setShowJoin(true)}
          >
            <LogIn className="h-4 w-4 mr-2" />
            Join
          </Button>
          <Button 
            className="bg-rose-600 hover:bg-rose-500"
            onClick={() => setShowCreate(true)}
            disabled={!limits?.can_create}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create
          </Button>
        </div>
      </div>

      {/* Pro Upgrade Banner (for free users at limit) */}
      {limits && !limits.can_create && !limits.is_pro && (
        <Card className="bg-gradient-to-r from-amber-950/30 to-slate-900 border-amber-500/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <AlertCircle className="h-8 w-8 text-amber-400" />
              <div className="flex-1">
                <p className="text-amber-300 font-medium">Free limit reached</p>
                <p className="text-sm text-slate-400">Upgrade to Pro for unlimited mini leagues and up to 500 players per league.</p>
              </div>
              <Button variant="outline" className="border-amber-500/30 text-amber-400">
                Upgrade to Pro
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* League of the Week Spotlight */}
      {leagueOfWeek && (
        <Card className="bg-gradient-to-r from-amber-950/40 via-rose-950/30 to-slate-900 border-amber-500/30 overflow-hidden relative" data-testid="league-of-the-week">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="py-5 relative">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/30 to-amber-600/10 border border-amber-500/20">
                <Sparkles className="h-7 w-7 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-amber-400/80">League of the Week</span>
                  {leagueOfWeek.pro_only && (
                    <Badge className="bg-amber-500/20 text-amber-400 text-xs"><Crown className="h-3 w-3 mr-1" />Pro</Badge>
                  )}
                </div>
                <h3 className="text-lg font-bold text-white truncate">{leagueOfWeek.name}</h3>
                {leagueOfWeek.description && (
                  <p className="text-sm text-slate-400 mt-0.5 line-clamp-1">{leagueOfWeek.description}</p>
                )}
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5">
                    <Users className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-300">{leagueOfWeek.member_count}<span className="text-slate-500">/{leagueOfWeek.max_members}</span></span>
                  </div>
                  <div className="text-sm text-emerald-400 font-medium">{leagueOfWeek.activity_rate}% active</div>
                  <div className="text-sm text-slate-400">by {leagueOfWeek.owner_username}</div>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                {leagueOfWeek.top_players?.slice(0, 3).map((p, i) => (
                  <div key={i} className="text-center px-2">
                    <div className={`text-xs font-bold ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-slate-300' : 'text-amber-600'}`}>
                      #{i + 1}
                    </div>
                    <div className="text-xs text-white truncate max-w-[72px]">{p.username}</div>
                    <div className="text-xs text-slate-500">{p.points} pts</div>
                  </div>
                ))}
              </div>
              {leagueOfWeek.is_public && leagueOfWeek.invite_code && (
                <Button 
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-500 flex-shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(leagueOfWeek.invite_code);
                    toast.success(`Invite code copied: ${leagueOfWeek.invite_code}`);
                  }}
                  data-testid="lotw-copy-code"
                >
                  <Copy className="h-4 w-4 mr-1.5" /> Join
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* League Cards */}
      {leagues.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {leagues.map(league => (
            <MiniLeagueCard
              key={league.id}
              league={league}
              onViewDetails={handleViewDetails}
              onLeave={handleLeave}
              onDelete={(l) => setConfirmDelete(l)}
              onCopyCode={copyInviteCode}
              onViewAnalytics={(l) => setAnalyticsLeague(l)}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <Trophy className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Mini Leagues Yet</h3>
            <p className="text-slate-400 mb-4">
              Create your own league or join one with an invite code
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => setShowJoin(true)}>
                <LogIn className="h-4 w-4 mr-2" />
                Join with Code
              </Button>
              <Button className="bg-rose-600 hover:bg-rose-500" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create League
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <CreateMiniLeagueDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        limits={limits}
        onCreate={handleCreate}
      />

      {/* Join Dialog */}
      <JoinMiniLeagueDialog
        open={showJoin}
        onClose={() => setShowJoin(false)}
        onJoin={handleJoin}
      />

      {/* Leaderboard Dialog */}
      <Dialog open={!!selectedLeague} onOpenChange={() => setSelectedLeague(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-rose-400" />
              {selectedLeague?.name} Leaderboard
            </DialogTitle>
            <DialogDescription>
              {selectedLeague?.member_count} participants • {formatTimeRemaining(selectedLeague?.time_remaining)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[50vh] space-y-2 pr-2">
            {leaderboardLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-rose-400" />
              </div>
            ) : leaderboard?.entries?.length > 0 ? (
              leaderboard.entries.map((entry) => (
                <LeaderboardRow key={entry.user_id} entry={entry} />
              ))
            ) : (
              <div className="text-center py-8 text-slate-400">
                No scores yet - play to get on the board!
              </div>
            )}
          </div>
          
          {leaderboard?.your_rank && (
            <div className="mt-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
              <div className="flex items-center justify-between">
                <span className="text-rose-400">Your Position</span>
                <span className="font-bold text-white">
                  #{leaderboard.your_rank} of {leaderboard.total_participants}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete League?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete "{confirmDelete?.name}" and remove all members. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDelete(confirmDelete)}
            >
              Delete League
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <LeagueAnalyticsDialog
        league={analyticsLeague}
        open={!!analyticsLeague}
        onClose={() => setAnalyticsLeague(null)}
        token={token}
      />
    </div>
  );
};

export default MiniLeaguesDashboard;
